import { currentUserQuery } from "@common/api/users"
import { hasPermission } from "@common/lib/permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { PageContent, PageTitle } from "@/components/page/page-content"
import useCustomToast from "@/hooks/use-custom-toast"
import { SetPinForm } from "./-components/form/set-pin-form"
import { PinActivity } from "./-components/pin-activity"
import { RemovePin } from "./-components/remove-pin"
import { SIGNING_PIN_PERMISSION } from "./-lib/permission"

export const Route = createFileRoute(
  "/_layout/settings/_panel/account/signing-pin/",
)({
  staticData: {
    breadcrumb: [{ label: "Account - Signing PIN" }],
  },
  beforeLoad: async ({ context: { queryClient } }) => {
    const user = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(user, SIGNING_PIN_PERMISSION)) {
      throw redirect({ to: "/settings/workspace/general" })
    }
  },
  component: SigningPinPage,
})

function SigningPinPage(): React.ReactElement {
  const { data: user } = useSuspenseQuery(currentUserQuery())
  const showToast = useCustomToast()

  return (
    <PageContent>
      <PageTitle
        title="Signing PIN"
        description="Your PIN applies your electronic signature when you sign off work."
      />
      {user.signing_pin_changed_at && (
        <PinActivity
          changedAt={user.signing_pin_changed_at}
          locked={user.signing_pin_locked}
          lastUsedAt={user.signing_pin_last_used_at}
        />
      )}
      <SetPinForm
        hasPin={user.has_signing_pin}
        onSet={() =>
          showToast("Saved", "Your signing PIN has been saved.", "success")
        }
      />
      {user.has_signing_pin && (
        <RemovePin
          onRemove={() =>
            showToast(
              "Removed",
              "Your signing PIN has been removed.",
              "success",
            )
          }
        />
      )}
    </PageContent>
  )
}
